import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { SUBSCRIPTION_PLANS, getPlanById, isEnterprisePlan, formatPrice, calculateAnnualSavings } from '../lib/subscription-plans';
import type { Profile } from '../types';

interface PricingPageProps {
  session: any;
  profile: Profile | null;
}

const PricingPage: React.FC<PricingPageProps> = ({ session, profile }) => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  // Get user's current plan
  const currentPlan = profile?.subscription?.plan_name || 'free';

  // Check for successful checkout return and refresh profile
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const success = urlParams.get('success');
    
    if (sessionId && success === 'true') {
      console.log('[PricingPage] Checkout success detected, refreshing profile...');
      // Dispatch event to refresh profile in parent App component
      window.dispatchEvent(new Event('refreshProfileRequested'));
      
      // Clean URL
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
      
      toast.success('Subscription updated successfully!');
    }
  }, []);

  // Handle subscription change
  const handleSubscribe = async (planId: string) => {
    if (!session) {
      toast.error('Please log in to subscribe');
      return;
    }

    if (isEnterprisePlan(planId)) {
      toast.info('Contact us for enterprise plans');
      return;
    }

    setSelectedPlan(planId);
    setLoading(true);

    try {
      const plan = getPlanById(planId);
      if (!plan) {
        throw new Error('Invalid plan selected');
      }

      // Select the appropriate price ID based on billing period
      const priceId = plan.stripePriceIds[billingPeriod];
      if (!priceId) {
        throw new Error('Price ID not configured for this billing period');
      }

      // Create checkout session
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          priceId,
          planName: planId,
          billingPeriod,
          userId: session.user?.id ?? null,
        })
      });

      // Check if response is OK before trying to parse JSON
      if (!response.ok) {
        let errorMessage = 'Failed to create checkout session';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          // If we can't parse the error response, use the status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Try to parse the success response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error('Invalid response from server');
      }

      // Redirect to Stripe checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to start subscription process');
      setSelectedPlan(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle plan change (upgrade/downgrade)
  const handleChangePlan = async (newPlanId: string) => {
    if (!session) {
      toast.error('Please log in to change plans');
      return;
    }

    if (isEnterprisePlan(newPlanId)) {
      toast.info('Contact us for enterprise plans');
      return;
    }

    setSelectedPlan(newPlanId);
    setLoading(true);

    try {
      const plan = getPlanById(newPlanId);
      if (!plan) {
        throw new Error('Invalid plan selected');
      }

      // Select the appropriate price ID based on billing period
      const priceId = plan.stripePriceIds[billingPeriod];
      if (!priceId) {
        throw new Error('Price ID not configured for this billing period');
      }

      // For plan changes, we'll use the same checkout session but with additional metadata
      // This will create a new subscription but we can handle the transition in the webhook
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          priceId,
          planName: newPlanId,
          billingPeriod,
          userId: session.user?.id ?? null,
          isPlanChange: true,
          currentPlan: currentPlan
        })
      });

      // Check if response is OK before trying to parse JSON
      if (!response.ok) {
        let errorMessage = 'Failed to create plan change session';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          // If we can't parse the error response, use the status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Try to parse the success response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error('Invalid response from server');
      }

      if (data.url) {
        // Stripe requires additional confirmation
        window.location.href = data.url;
        return;
      }

      if (data.success || data.subscription) {
        toast.success('Your plan has been changed successfully!');
        // Refresh the page to show updated plan
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        return;
      }

      throw new Error('No subscription data received');
    } catch (error) {
      console.error('Plan change error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to start plan change process');
      setSelectedPlan(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-luna-bg-light dark:bg-luna-bg-dark py-12 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-extrabold text-luna-text-light dark:text-luna-text-dark sm:text-5xl sm:tracking-tight">
            Simple, transparent pricing
          </h1>
          <p className="mt-5 max-w-xl mx-auto text-xl text-luna-text-light/70 dark:text-luna-text-dark/70">
            Choose the perfect plan for your legal needs. All plans include core features with no hidden fees.
          </p>
          
          {/* Billing Toggle */}
          <div className="mt-10 flex items-center justify-center">
            <span className={`mr-4 text-sm font-medium ${billingPeriod === 'monthly' ? 'text-luna-text-light dark:text-luna-text-dark' : 'text-luna-text-light/50 dark:text-luna-text-dark/50'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
              className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-luna-secondary transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-luna-secondary focus:ring-offset-2"
              role="switch"
              aria-checked={billingPeriod === 'annual'}
            >
              <span className="sr-only">Use setting</span>
              <span
                aria-hidden="true"
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  billingPeriod === 'annual' ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
            <span className={`ml-4 text-sm font-medium ${billingPeriod === 'annual' ? 'text-luna-text-light dark:text-luna-text-dark' : 'text-luna-text-light/50 dark:text-luna-text-dark/50'}`}>
              Annual <span className="text-xs bg-luna-secondary/20 text-luna-secondary px-2 py-1 rounded-full ml-1">Save 20%</span>
            </span>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="mt-12 space-y-4 sm:mt-16 sm:space-y-0 sm:grid sm:grid-cols-1 sm:gap-6 lg:max-w-4xl lg:mx-auto xl:max-w-none xl:mx-0 xl:grid-cols-5">
          {SUBSCRIPTION_PLANS.map((plan) => {
            const isCurrentPlan = plan.id === currentPlan;
            const isUpgrade = plan.id !== 'free' && currentPlan === 'free';
            const isDowngrade = plan.id === 'free' && currentPlan !== 'free';
            const annualSavings = calculateAnnualSavings(plan);
            
            return (
              <div
                key={plan.id}
                className={`border rounded-2xl shadow-sm overflow-hidden ${
                  plan.popular
                    ? 'border-luna-secondary ring-2 ring-luna-secondary/20'
                    : 'border-luna-text-light/20 dark:border-luna-text-dark/30'
                } ${plan.comingSoon ? 'opacity-70' : ''}`}
              >
                {plan.popular && (
                  <div className="bg-luna-secondary py-2 px-4">
                    <p className="text-center text-sm font-semibold text-luna-primary">Most popular</p>
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-lg font-bold text-luna-text-light dark:text-luna-text-dark capitalize">
                    {plan.name}
                    {plan.comingSoon && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Coming soon
                      </span>
                    )}
                  </h3>
                  <p className="mt-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                    {plan.description}
                  </p>
                  
                  <div className="mt-4">
                    {plan.monthlyPrice === null ? (
                      <p className="text-4xl font-extrabold text-luna-text-light dark:text-luna-text-dark">
                        Custom
                      </p>
                    ) : (
                      <>
                        <p className="text-4xl font-extrabold text-luna-text-light dark:text-luna-text-dark">
                          {formatPrice(billingPeriod === 'annual' && plan.annualPrice !== null ? plan.annualPrice : plan.monthlyPrice)}
                          <span className="text-lg font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">
                            /{billingPeriod === 'annual' ? 'year' : 'month'}
                          </span>
                        </p>
                        {billingPeriod === 'annual' && plan.monthlyPrice !== null && plan.annualPrice !== null && (
                          <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 mt-1">
                            Billed annually (${(plan.annualPrice * 12).toFixed(2)}/year)
                            {annualSavings && (
                              <span className="block text-luna-secondary font-medium">
                                Save ${annualSavings.toFixed(2)} per year
                              </span>
                            )}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  
                  <ul className="mt-6 space-y-4">
                    {plan.features.map((feature) => (
                      <li key={feature.name} className="flex items-start">
                        <span className="material-symbols-outlined text-luna-secondary text-lg mr-2">
                          {feature.included ? 'check' : 'close'}
                        </span>
                        <span className="text-sm text-luna-text-light dark:text-luna-text-dark">
                          {feature.name}
                          {feature.limit && ` (${feature.limit})`}
                          {feature.description && ` - ${feature.description}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="mt-8">
                    {plan.comingSoon ? (
                      <button
                        disabled
                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-luna-primary bg-luna-secondary opacity-50 cursor-not-allowed"
                      >
                        Contact Sales
                      </button>
                    ) : isCurrentPlan ? (
                      <button
                        disabled
                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-luna-primary bg-luna-secondary opacity-75 cursor-not-allowed"
                      >
                        Current Plan
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (isCurrentPlan) return;
                          if (currentPlan !== 'free') {
                            handleChangePlan(plan.id);
                          } else {
                            handleSubscribe(plan.id);
                          }
                        }}
                        disabled={loading && selectedPlan === plan.id}
                        className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-luna-primary ${
                          isCurrentPlan 
                            ? 'bg-luna-secondary opacity-75 cursor-not-allowed' 
                            : 'bg-luna-secondary hover:bg-luna-secondary/90'
                        }`}
                      >
                        {loading && selectedPlan === plan.id ? (
                          <>
                            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-luna-primary mr-2"></span>
                            Processing...
                          </>
                        ) : isCurrentPlan ? (
                          'Current Plan'
                        ) : isUpgrade ? (
                          'Upgrade'
                        ) : isDowngrade ? (
                          'Downgrade'
                        ) : (
                          'Get Started'
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-luna-text-light dark:text-luna-text-dark mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white dark:bg-luna-primary rounded-lg p-6 shadow">
              <h3 className="text-lg font-medium text-luna-text-light dark:text-luna-text-dark">
                Can I change my plan anytime?
              </h3>
              <p className="mt-2 text-luna-text-light/70 dark:text-luna-text-dark/70">
                Yes, you can upgrade or downgrade your plan at any time. When upgrading, you'll be charged a prorated amount for the remainder of your billing cycle. When downgrading, changes take effect at the end of your current billing period.
              </p>
            </div>
            
            <div className="bg-white dark:bg-luna-primary rounded-lg p-6 shadow">
              <h3 className="text-lg font-medium text-luna-text-light dark:text-luna-text-dark">
                What payment methods do you accept?
              </h3>
              <p className="mt-2 text-luna-text-light/70 dark:text-luna-text-dark/70">
                We accept all major credit cards including Visa, Mastercard, American Express, and Discover. All payments are processed securely through Stripe.
              </p>
            </div>
            
            <div className="bg-white dark:bg-luna-primary rounded-lg p-6 shadow">
              <h3 className="text-lg font-medium text-luna-text-light dark:text-luna-text-dark">
                Is there a free trial?
              </h3>
              <p className="mt-2 text-luna-text-light/70 dark:text-luna-text-dark/70">
                Yes! All new users get access to our Free plan which includes basic features. You can upgrade to a paid plan at any time to unlock additional features.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;